import { interleaveTwoArrays } from '@eventbrite/ads';
import { deepKeysToCamel } from '@eventbrite/transformation-utils';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import takeRight from 'lodash/takeRight';
import {
    ENTITY_TYPE_MAP,
    EVENT_CARD_TYPE,
    PROFILE_CARD_TYPE,
} from '../../../../constants';
import {
    BUCKETS_NAMES,
    LIKED_EVENTS,
    ORGANIZERS_TO_FOLLOW,
    PRIMARY_BUCKET,
    SECONDARY_BUCKET,
} from '../../../../constants/constants';
import { ALL_TAB, FOR_YOU } from '../../../../constants/tabConfig';
import {
    ContentShape,
    Events,
    OrganizerProfile,
} from '../../../../types/index';
import { eventsCardInfoLookup } from '../../../../utils/general';
import { HOME_TABS_SLOTS } from '../constants';

const CARD_INFO_LOOKUP_MAP: { [key: string]: any } = {
    [EVENT_CARD_TYPE]: eventsCardInfoLookup,
};

export const getBucketsContent = (
    { entityContext, entities }: ContentShape,
    currencyFormat: string,
) => {
    // buckets contains all dynamic buckets that are responsible for requesting their own content
    const buckets = [
        {
            key: ORGANIZERS_TO_FOLLOW,
            type: PROFILE_CARD_TYPE,
            bucketContent: {
                name: '',
                description:
                    'Follow the most popular local organizers and get notified when they create events.',
            },
        },
    ];

    // eventsBuckets contains all buckets recieved from the backend
    const eventBuckets = entityContext?.map(({ type, ...bucketContent }) => {
        let result: any = {
            type,
            ...bucketContent,
        };

        if (type === 'profile') {
            const bucketKey = 'profiles';
            const bucketsContent = bucketContent[bucketKey]?.map(
                (id?: string) => entities[id || ''],
            );

            const profiles = bucketsContent?.map(
                (profile: OrganizerProfile) => ({
                    id: profile.id,
                    name: profile.name,
                    url: profile.url,
                    followedByYou: profile.followedByYou,
                    profilePicture: profile?.image?.url,
                    numUpcomingEvents: profile.numUpcomingEvents,
                    numFollowers: profile.numFollowers,
                }),
            );

            result = {
                ...result,
                profiles,
            };
        }

        if (
            has(ENTITY_TYPE_MAP, type || '') &&
            has(CARD_INFO_LOOKUP_MAP, type || '')
        ) {
            const { name: bucketName, key: bucketKey } = bucketContent;
            const entityKey = ENTITY_TYPE_MAP[type || ''];

            if (!includes(BUCKETS_NAMES, bucketKey)) {
                // eslint-disable-next-line no-console
                console.warn('Missing Affiliate Code', {
                    bucketName,
                    bucketKey,
                });
            }

            if (bucketContent[entityKey]) {
                const bucketsContent = bucketContent[entityKey].map(
                    (id: string) => entities[id],
                );
                const events = CARD_INFO_LOOKUP_MAP[type || '']({
                    bucketKey,
                    bucketsContent,
                    currencyFormat,
                });

                result = {
                    ...result,
                    events,
                };
            }
        }

        return result;
    });

    return flatten([buckets, eventBuckets]);
};

export const getFlatBucketContent = (
    tabKey: string,
    flatBucket?: { results: Events; promoted_results: Events },
    currencyFormat?: string,
    _featureFlags = {},
) => {
    const promotedEvents = deepKeysToCamel(flatBucket?.promoted_results) || [];
    const organicEvents = deepKeysToCamel(flatBucket?.results) || [];
    const events = interleaveTwoArrays(
        organicEvents,
        promotedEvents,
        HOME_TABS_SLOTS,
    );
    const shouldShowOnboardingEntryPoint = tabKey === ALL_TAB;
    const firstBucketEventsNumber = shouldShowOnboardingEntryPoint ? 8 : 9;
    const primaryBucketKey = tabKey === FOR_YOU ? LIKED_EVENTS : PRIMARY_BUCKET;

    let eventBuckets: any = [];

    if (!isEmpty(events)) {
        const primaryBucketEvents = take(events, firstBucketEventsNumber);

        const primaryBucketParsedEvents = eventsCardInfoLookup({
            bucketsContent: primaryBucketEvents,
            currencyFormat,
            bucketKey: primaryBucketKey,
        });

        eventBuckets = [
            {
                events: primaryBucketParsedEvents,
                key: primaryBucketKey,
                name: 'bucketName',
                template: 'card_small_8',
                type: 'event',
            },
        ];

        if (events.length >= firstBucketEventsNumber && tabKey !== FOR_YOU) {
            /**
             * As we are showing an extra card on the first bucket, we will also have the last card
             * on the second bucket.
             * Using CSS and breakpoints we will show one and hide the other one to match the grid.
             * As we get one more event from the left on the second bucket, we also have to get one
             * more event to the right.
             */
            const secondaryBucketStartingPoint =
                events.length - firstBucketEventsNumber + 1;
            const secondaryBucketEvents = take(
                takeRight(events, secondaryBucketStartingPoint),
                17,
            );
            // if this number is negative, returns []

            const secondaryBucketParsedEvents = eventsCardInfoLookup({
                bucketsContent: secondaryBucketEvents,
                currencyFormat,
                bucketKey: SECONDARY_BUCKET,
            });

            eventBuckets.push({
                events: secondaryBucketParsedEvents,
                key: SECONDARY_BUCKET,
                name: 'bucketName',
                template: 'card_small_8',
                type: 'event',
            });
        }
    }

    return eventBuckets;
};
